import { Button, ButtonProps } from '@/components/common/Button';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { ArrowRight } from '@/components/common/icons';
import Icon from '@/components/common/icons/Icon';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Text } from '@/components/common/Typography';
import { Box, Center, FlexProps, Spacer } from '@chakra-ui/react';
import { useCurrentRateInfo } from '@common/react/rates';
import { dt } from '@common/util/dateTime';
import { ParentSize } from '@visx/responsive';
import GoalsChart from '../GoalsChart';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

const RetirementGoalChartContainer = (props: FlexProps) => {
    const { currentRateInfo } = useCurrentRateInfo();
    const { includedAccounts, partnerSwitcherType } =
        useRetirementGoalContext();

    // If no accounts are included yet, show custom empty state
    if (includedAccounts.length === 0 || !currentRateInfo) {
        return partnerSwitcherType === 'MINE' ? (
            <EmptyStateMine {...props} />
        ) : partnerSwitcherType === 'SHARED' ? (
            <EmptyStateShared {...props} />
        ) : null; // TODO: Custom empty state for partner? Might not be needed
    }

    // FIXME: We need to reimplement this without client-side VSGs
    //
    // Otherwise, show actual chart
    // return isSharedGoal ? (
    //     <GoalDetailChartViewSharedRetirementGoal
    //         {...props}
    //         currentRateInfo={currentRateInfo}
    //     />
    // ) : (
    //     <GoalDetailChartViewPrivateOrPersonalRetirementGoal
    //         currentRateInfo={currentRateInfo}
    //         {...props}
    //     />
    // );

    return null;
};

const EmptyStateMine = (props: FlexProps) => {
    const { goalConfig, setPosition } = useRetirementGoalContext();

    return (
        <FlexCol
            justifyContent="space-between"
            w="full"
            h={268}
            pt={12}
            pb={10}
            px={10}
            bg={goalConfig.color}
            {...props}
        >
            <FlexRow justifyContent="space-between">
                <FlexCol>
                    <Text fontSize="xl" fontWeight="medium" color="white">
                        Get your retirement money
                    </Text>
                    <Text fontSize="xl" fontWeight="medium" color="white">
                        working for you in the right way.
                    </Text>
                </FlexCol>
                <Icon name={goalConfig.iconName} size={10} color="white" />
            </FlexRow>
            <FlexCol gap={4}>
                <Text fontSize="sm" color="whiteAlpha.500">
                    Accounts you can open within your goal
                </Text>
                <FlexRow>
                    <RetirementAccountTypeButton
                        onClick={() => setPosition('OPEN_TRADITIONAL_IRA')}
                        onClickEvent="Show Traditional IRA Button Clicked"
                    >
                        Traditional IRA
                    </RetirementAccountTypeButton>
                    <RetirementAccountTypeButton
                        onClick={() => setPosition('OPEN_ROTH_IRA')}
                        onClickEvent="Show Roth IRA Button Clicked"
                    >
                        Roth IRA
                    </RetirementAccountTypeButton>
                    <Spacer />
                </FlexRow>
            </FlexCol>
        </FlexCol>
    );
};

const RetirementAccountTypeButton = (props: ButtonProps) => {
    return (
        <Button
            variant="pill"
            size="xs"
            colorScheme="transparent"
            color="white"
            border="1.5px solid white"
            mr={2}
            {...hoverStyleProps({ bg: 'blackAlpha.100' })}
            {...props}
        />
    );
};

const EmptyStateShared = (props: FlexProps) => {
    const { setPartnerSwitcherType } = useRetirementGoalContext();

    return (
        <FlexCol shrink={0} grow={0} {...props}>
            <FlexRow shrink={0} grow={0}>
                <Box h={280} w="full" position="relative">
                    <Center
                        position="absolute"
                        w="full"
                        h="full"
                        top={0}
                        left={0}
                        zIndex={9}
                    >
                        <Button
                            variant="ghost"
                            onClick={() => setPartnerSwitcherType('MINE')}
                            onClickEvent="Add Accounts Button Clicked"
                            onClickEventParams={{
                                location: 'Retirement Goal Chart',
                            }}
                            px={4}
                            py={4}
                            mb={8}
                            alignItems="center"
                            color="textMuted"
                            bg="white"
                            {...hoverStyleProps({ bg: 'gray.100' })}
                        >
                            <FlexCol>
                                <Text>Add accounts to track your</Text>
                                <Text>
                                    shared retirement
                                    <ArrowRight size="sm" />
                                </Text>
                            </FlexCol>
                        </Button>
                    </Center>
                    <ParentSize>
                        {(parent) => (
                            <ErrorBoundary>
                                <GoalsChart
                                    entities={[]}
                                    width={parent.width}
                                    height={parent.height}
                                    range={{
                                        startDate: dt()
                                            .startOf('year')
                                            .toDateStr(),
                                        endDate: dt()
                                            .startOf('year')
                                            .add(10, 'years')
                                            .toDateStr(),
                                        unit: 'year',
                                    }}
                                />
                            </ErrorBoundary>
                        )}
                    </ParentSize>
                </Box>
            </FlexRow>
        </FlexCol>
    );
};

export default RetirementGoalChartContainer;
